import React, { useEffect, useState } from "react";
import { supabase, fmtBRL, formatError } from "@/lib/supabase";
import { db } from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const COLS = [
  { id: "novo", label: "Novo" },
  { id: "contato feito", label: "Contato feito" },
  { id: "proposta enviada", label: "Proposta enviada" },
  { id: "fechado", label: "Fechado" },
  { id: "perdido", label: "Perdido" },
];

export default function CRM() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", telefone: "", servico_interesse: "", valor_estimado: 0, status: "novo" });

  const reload = async () => {
    try { setItems((await db.select("leads", { order: "created_at" })) || []); }
    catch (err) { toast.error(formatError(err)); }
  };
  useEffect(() => { if (user) reload(); }, [user]);

  const create = async () => {
    try {
      await db.insert("leads", { ...form, valor_estimado: Number(form.valor_estimado), user_id: user.id });
      toast.success("Lead adicionado");
      setOpen(false);
      setForm({ nome: "", email: "", telefone: "", servico_interesse: "", valor_estimado: 0, status: "novo" });
      reload();
    } catch (err) { toast.error(formatError(err)); }
  };
  const move = async (id, status) => { try { await db.update("leads", id, { status }); reload(); } catch (err) { toast.error(formatError(err)); } };
  const remove = async (id) => { try { await db.remove("leads", id); reload(); } catch (err) { toast.error(formatError(err)); } };

  return (
    <div data-testid="crm-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">CRM</div>
          <h1 className="font-serif text-4xl md:text-5xl">Pipeline de vendas</h1>
          <p className="text-[#4A4A4A] mt-2">Arraste leads visualmente pelo funil.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white" data-testid="new-lead-btn">
              <Plus className="w-4 h-4 mr-2" /> Novo lead
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Novo lead</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <div><Label className="text-xs font-mono uppercase">Nome</Label>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="rounded-none" data-testid="lead-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-mono uppercase">E-mail</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" /></div>
                <div><Label className="text-xs font-mono uppercase">Telefone</Label>
                  <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} className="rounded-none" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-mono uppercase">Serviço de interesse</Label>
                  <Input value={form.servico_interesse} onChange={(e) => setForm({ ...form, servico_interesse: e.target.value })} className="rounded-none" /></div>
                <div><Label className="text-xs font-mono uppercase">Valor estimado (R$)</Label>
                  <Input type="number" value={form.valor_estimado} onChange={(e) => setForm({ ...form, valor_estimado: e.target.value })} className="rounded-none" /></div>
              </div>
              <div><Label className="text-xs font-mono uppercase">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{COLS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <Button onClick={create} className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white w-full" data-testid="lead-save-btn">Adicionar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {COLS.map((col) => {
          const cards = items.filter((l) => l.status === col.id);
          return (
            <div key={col.id} className="bg-[#EFECE6] border border-black/10 p-3 min-h-[400px]" data-testid={`column-${col.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-xs uppercase tracking-widest">{col.label}</div>
                <div className="font-mono text-xs text-[#8A8A8A]">{cards.length}</div>
              </div>
              <div className="space-y-2">
                {cards.map((l) => (
                  <Card key={l.id} className="p-3 bg-white border border-black/10 rounded-none text-sm">
                    <div className="font-serif text-lg leading-tight">{l.nome}</div>
                    {l.servico_interesse && <div className="text-xs text-[#8A8A8A] mt-1">{l.servico_interesse}</div>}
                    {!!l.valor_estimado && <div className="font-mono text-xs mt-2 text-[#D9381E]">{fmtBRL(l.valor_estimado)}</div>}
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-black/10">
                      <Select value={l.status} onValueChange={(v) => move(l.id, v)}>
                        <SelectTrigger className="rounded-none h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>{COLS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <button onClick={() => remove(l.id)} className="text-[#D9381E] hover:text-[#B82914]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
